// Types
import type {
  TAutomatedEmailDefaultTemplateSeed,
  TAutomatedEmailDefaultTemplatesResponse,
  TAutomatedEmailSaleType,
  TAutomatedEmailUserTemplateApi,
  TAutomatedEmailUserTemplateStoreRow,
  TAutomatedEmailUserTriggerApi,
  TAutomatedEmailUserTriggerSyncRow,
} from '~/modules/opportunities/automated-email/types';
import type {
  TAutomatedEmailUserTriggerSyncRowTyped,
  TKnownUserTriggerName,
} from '~/modules/opportunities/automated-email/settings/types';

// Models
import { TriggersModel } from '~/modules/opportunities/automated-email/settings/models/triggers';

// Config
import { WITHDRAWN_TEMPLATES_ENABLED } from '~/modules/opportunities/automated-email/settings/config';

// Utils
import { isKnownUserTriggerName } from '~/modules/opportunities/automated-email/settings/utils/triggers-api-map';

const BASE_DEFAULT_USER_TRIGGERS_SYNC: readonly TAutomatedEmailUserTriggerSyncRowTyped[] = [
  { trigger_name: 'reduced', is_active: true },
  { trigger_name: 'market-success', is_active: true },
];

const WITHDRAWN_DEFAULT_USER_TRIGGER: TAutomatedEmailUserTriggerSyncRowTyped = {
  trigger_name: 'withdrawn',
  is_active: true,
};

const getDefaultUserTriggersSync = (
  saleType: TAutomatedEmailSaleType
): readonly TAutomatedEmailUserTriggerSyncRowTyped[] =>
  WITHDRAWN_TEMPLATES_ENABLED && saleType === 'sale'
    ? [...BASE_DEFAULT_USER_TRIGGERS_SYNC, WITHDRAWN_DEFAULT_USER_TRIGGER]
    : BASE_DEFAULT_USER_TRIGGERS_SYNC;

export type TAutomatedEmailTriggersAndTemplatesResult = {
  triggers: TAutomatedEmailUserTriggerApi[];
  templates: TAutomatedEmailUserTemplateApi[];
};

const triggersAndTemplatesEnsureInFlight = new Map<
  string,
  Promise<TAutomatedEmailTriggersAndTemplatesResult>
>();

function ensureScopeKey(patchId: number, saleType: TAutomatedEmailSaleType): string {
  return `${patchId}:${saleType}`;
}

function getTriggersAndTemplatesEnsureInFlight(
  patchId: number,
  saleType: TAutomatedEmailSaleType
): Promise<TAutomatedEmailTriggersAndTemplatesResult> | undefined {
  return triggersAndTemplatesEnsureInFlight.get(ensureScopeKey(patchId, saleType));
}

function setTriggersAndTemplatesEnsureInFlight(
  patchId: number,
  saleType: TAutomatedEmailSaleType,
  next: Promise<TAutomatedEmailTriggersAndTemplatesResult> | undefined
): void {
  const key: string = ensureScopeKey(patchId, saleType);

  if (next === undefined) {
    triggersAndTemplatesEnsureInFlight.delete(key);

    return;
  }

  triggersAndTemplatesEnsureInFlight.set(key, next);
}

function isDefaultTemplateSeed(value: unknown): value is TAutomatedEmailDefaultTemplateSeed {
  if (value === null || typeof value !== 'object') {
    return false;
  }

  const rec: Record<string, unknown> = value as Record<string, unknown>;

  return typeof rec.subject === 'string' && typeof rec.content === 'string';
}

function getDesiredTriggerNames(
  saleType: TAutomatedEmailSaleType
): ReadonlySet<TKnownUserTriggerName> {
  return new Set<TKnownUserTriggerName>(
    getDefaultUserTriggersSync(saleType).map(
      (trigger: TAutomatedEmailUserTriggerSyncRowTyped): TKnownUserTriggerName =>
        trigger.trigger_name
    )
  );
}

export function buildStoreRowsFromDefaults(
  apiTriggers: TAutomatedEmailUserTriggerApi[],
  defaults: TAutomatedEmailDefaultTemplatesResponse
): TAutomatedEmailUserTemplateStoreRow[] {
  const rows: TAutomatedEmailUserTemplateStoreRow[] = [];
  const triggerByName: Map<string, TAutomatedEmailUserTriggerApi> = new Map(
    apiTriggers.map((trigger: TAutomatedEmailUserTriggerApi): [string, TAutomatedEmailUserTriggerApi] => [
      trigger.trigger_name,
      trigger,
    ])
  );

  for (const triggerName of triggerByName.keys()) {
    const items: TAutomatedEmailDefaultTemplateSeed[] | undefined = defaults[triggerName];

    if (items === undefined || items.length === 0) {
      continue;
    }

    const trigger: TAutomatedEmailUserTriggerApi | undefined = triggerByName.get(triggerName);

    if (trigger === undefined) {
      continue;
    }

    for (const raw of items) {
      if (!isDefaultTemplateSeed(raw)) {
        continue;
      }

      const row: TAutomatedEmailUserTemplateStoreRow = {
        patch_trigger_id: trigger.id,
        subject: raw.subject,
        content: raw.content,
        is_active: false,
      };

      rows.push(row);
    }
  }

  return rows;
}

/**
 * Ensures the backend has all enabled triggers and seeds defaults only for triggers that do not
 * have templates yet.
 */
export function ensureAutomatedEmailTriggersAndTemplates(
  model: TriggersModel,
  patchId: number,
  saleType: TAutomatedEmailSaleType
): Promise<TAutomatedEmailTriggersAndTemplatesResult> {
  const existing: Promise<TAutomatedEmailTriggersAndTemplatesResult> | undefined =
    getTriggersAndTemplatesEnsureInFlight(patchId, saleType);

  if (existing !== undefined) {
    return existing;
  }

  const run: Promise<TAutomatedEmailTriggersAndTemplatesResult> =
    runEnsureAutomatedEmailTriggersAndTemplates(model, patchId, saleType);

  setTriggersAndTemplatesEnsureInFlight(patchId, saleType, run);

  run.finally((): void => {
    setTriggersAndTemplatesEnsureInFlight(patchId, saleType, undefined);
  });

  return run;
}

async function runEnsureAutomatedEmailTriggersAndTemplates(
  model: TriggersModel,
  patchId: number,
  saleType: TAutomatedEmailSaleType
): Promise<TAutomatedEmailTriggersAndTemplatesResult> {
  let apiTriggers: TAutomatedEmailUserTriggerApi[] = await model.getPatchTriggers(
    patchId,
    saleType
  );

  const desiredTriggerNames: ReadonlySet<TKnownUserTriggerName> = getDesiredTriggerNames(saleType);
  const existingTriggerNames: ReadonlySet<string> = new Set<string>(
    apiTriggers.map((trigger: TAutomatedEmailUserTriggerApi): string => trigger.trigger_name)
  );
  const syncRows: TAutomatedEmailUserTriggerSyncRow[] = getDefaultUserTriggersSync(saleType).filter(
    (trigger: TAutomatedEmailUserTriggerSyncRowTyped): boolean =>
      !existingTriggerNames.has(trigger.trigger_name)
  );

  if (syncRows.length > 0) {
    await model.syncPatchTriggers(patchId, saleType, syncRows);
    apiTriggers = await model.getPatchTriggers(patchId, saleType);
  }

  let apiTemplates: TAutomatedEmailUserTemplateApi[] = await model.getPatchTemplates(
    patchId,
    saleType
  );

  const triggerIdsWithTemplates: ReadonlySet<number> = new Set<number>(
    apiTemplates.map(
      (template: TAutomatedEmailUserTemplateApi): number => template.patch_trigger_id
    )
  );
  const triggersWithoutTemplates: TAutomatedEmailUserTriggerApi[] = apiTriggers.filter(
    (trigger: TAutomatedEmailUserTriggerApi): boolean =>
      isKnownUserTriggerName(trigger.trigger_name) &&
      desiredTriggerNames.has(trigger.trigger_name) &&
      !triggerIdsWithTemplates.has(trigger.id)
  );

  if (triggersWithoutTemplates.length > 0) {
    const defaults: TAutomatedEmailDefaultTemplatesResponse = await model.getDefaultTemplates(
      saleType
    );
    const rows: TAutomatedEmailUserTemplateStoreRow[] = buildStoreRowsFromDefaults(
      triggersWithoutTemplates,
      defaults
    );

    if (rows.length > 0) {
      await model.storePatchTemplates(patchId, saleType, rows);
      // Re-fetch so local state cannot accumulate duplicates if store response shape changes.
      apiTemplates = await model.getPatchTemplates(patchId, saleType);
    }
  }

  if (saleType !== 'sale') {
    const withdrawnTriggerIds: ReadonlySet<number> = new Set<number>(
      apiTriggers
        .filter(
          (trigger: TAutomatedEmailUserTriggerApi): boolean => trigger.trigger_name === 'withdrawn'
        )
        .map((trigger: TAutomatedEmailUserTriggerApi): number => trigger.id)
    );

    apiTriggers = apiTriggers.filter(
      (trigger: TAutomatedEmailUserTriggerApi): boolean => trigger.trigger_name !== 'withdrawn'
    );
    apiTemplates = apiTemplates.filter(
      (template: TAutomatedEmailUserTemplateApi): boolean =>
        !withdrawnTriggerIds.has(template.patch_trigger_id)
    );
  }

  const result: TAutomatedEmailTriggersAndTemplatesResult = {
    triggers: apiTriggers,
    templates: apiTemplates,
  };

  return result;
}
